// Where the timeline's "נוצר כרטיס" rows come from (`buildCreatedEntries` turns each source into a
// row). Pure: no Firebase, no fetch.
import type { CreatedRowSource } from "../../../../crm/panel/agency/agencyPersonModel";
import type { REST_schema } from "../../../../../../@types/sharedSchema";

/** A create time as the wire carries it (an ISO string; a Date or a Timestamp-shaped object tolerated) as ISO, or null. */
const isoOrNull = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === "string" || value instanceof Date) {
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date.toISOString();
  }
  const seconds = (value as { _seconds?: number; seconds?: number })._seconds ?? (value as { seconds?: number }).seconds;
  return typeof seconds === "number" ? new Date(seconds * 1000).toISOString() : null;
};

/**
 * The copies the contact's own dossier names (`contactObjectsAcrossUsers`: the caller's own and every
 * colleague's the server returned), as sources for the "נוצר כרטיס" rows. `createdTime` survives every
 * pool tier on the server (`CONTACT_FIELDS_VISIBLE_AT_BASIC`).
 */
export function createdRowSourcesFromDossier(response: REST_schema.ContactDetailsResponseSchema | undefined): CreatedRowSource[] {
  if (!Array.isArray(response)) return [];
  return response.flatMap((detail) =>
    (detail.contactObjectsAcrossUsers ?? [])
      .filter((copy) => copy?.user?.userId)
      .map((copy) => ({
        ownerUserId: copy.user.userId,
        ownerName: copy.user.userName || null,
        ownerAvatarUrl: copy.user.userAvatar || null,
        createdTime: isoOrNull(copy.contact?.createdTime)
      }))
  );
}

/**
 * The "נוצר כרטיס" rows' sources: the agency roster's where it is drawn; else the dossier's own copies
 * — which is ~95% of contacts, the ones nobody else holds, and used to show no create row at all
 * (Haim, 2026-10-20: "why don't we see the created time?"). Under an agency title (`restrictToAgency`)
 * only the caller's own copy is added: every source also widens the feed's scope there, and the
 * dossier unions every agency the caller belongs to.
 */
export function createdRowSources(input: {
  agencyVisible: boolean;
  holders: readonly CreatedRowSource[];
  dossier: REST_schema.ContactDetailsResponseSchema | undefined;
  myUserId: string;
  restrictToAgency: boolean;
}): readonly CreatedRowSource[] {
  if (input.agencyVisible) return input.holders;
  const copies = createdRowSourcesFromDossier(input.dossier);
  return input.restrictToAgency ? copies.filter((copy) => copy.ownerUserId === input.myUserId) : copies;
}
